import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Button,
  Spinner,
  Text,
} from '@fluentui/react-components';
import {
  Dismiss20Regular,
  ReOrderDotsVertical20Regular,
  Open16Regular,
  ZoomIn16Regular,
  ZoomOut16Regular,
  ArrowReset20Regular,
  ArrowRotateClockwise20Regular,
  ArrowRotateCounterclockwise20Regular,
  Save20Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  // Non-modal floating window. position:fixed with NO backdrop, so the order
  // form + sidebar underneath stay fully interactive while it's open — the
  // whole point is to read the receipt and type values at the same time.
  window: {
    position: 'fixed',
    zIndex: 1400,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    boxShadow: tokens.shadow28,
    // CSS-native resize grip (bottom-right). Needs a non-visible overflow.
    resize: 'both',
    ...shorthands.overflow('hidden'),
    minWidth: '240px',
    minHeight: '200px',
  },
  // Embedded variant: same chrome, but rendered in-flow inside a host modal
  // rather than as a free-floating window. No fixed positioning, drag, or
  // resize — it fills its container so the preview reads as part of the modal.
  windowEmbedded: {
    position: 'relative',
    zIndex: 'auto',
    width: '100%',
    height: '60vh',
    minHeight: '360px',
    resize: 'none',
    boxShadow: 'none',
  },
  titleBar: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    ...shorthands.padding('6px', '8px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    cursor: 'move',
    // Don't let a drag select the title text.
    userSelect: 'none',
    touchAction: 'none',
  },
  // Embedded variant has nothing to drag, so the move cursor would mislead.
  titleBarStatic: {
    cursor: 'default',
  },
  dragIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  title: {
    flexGrow: 1,
    minWidth: 0,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '13px',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Current zoom readout between the zoom buttons. Fixed width so the buttons
  // don't shuffle as the percentage changes width (100% → 25%).
  zoomLabel: {
    flexShrink: 0,
    minWidth: '38px',
    textAlign: 'center',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    userSelect: 'none',
  },
  body: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    // auto (not hidden) so a zoomed-in image can be scrolled/panned into view
    // rather than clipped. At 100% it fits, so no scrollbars appear.
    ...shorthands.overflow('auto'),
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    // Scale around the center so zoom keeps the focal point roughly put.
    transformOrigin: 'center center',
    // Drag should move the window, not ghost-drag the image.
    userSelect: 'none',
    pointerEvents: 'none',
  },
  // PDFs render in the browser's native viewer (its own scroll + zoom), so the
  // frame just fills the body — the custom zoom controls don't apply.
  frame: {
    width: '100%',
    height: '100%',
    ...shorthands.border('none'),
  },
  // Optional editor strip below the image (e.g. caption + category for photos).
  // flex-shrink:0 so it keeps its height while the image area absorbs resize.
  footer: {
    flexShrink: 0,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('10px', '12px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

interface FloatingImageViewerProps {
  open: boolean;
  src: string | null;
  title?: string;
  onClose: () => void;
  /** Render `src` as a PDF (native <iframe> viewer) instead of an image. The
   *  custom zoom controls are hidden in this mode — the browser's PDF viewer
   *  brings its own. */
  isPdf?: boolean;
  /** When provided, shows rotate-left/right + a Save control. Receives the net
   *  clockwise rotation (a multiple of 90) the user dialed in; resolve once the
   *  rotated bytes are persisted. Omit to make the viewer read-only. */
  onSaveRotation?: (degrees: number) => Promise<void>;
  /** Optional content rendered in a strip beneath the image — used by the photo
   *  gallery to host the caption + category editor. Omitted (tonnage) → no strip. */
  footer?: React.ReactNode;
  /** Render in-flow inside a host modal (fills its container) instead of as a
   *  free-floating, draggable window. The drag/resize/positioning chrome is
   *  dropped; everything else (zoom, rotate, footer) is identical. */
  embedded?: boolean;
}

const DEFAULT_SIZE = { w: 640, h: 700 };

/**
 * Non-modal, draggable, resizable image viewer. Floats on top of the page with
 * no backdrop so whatever is underneath (a form, the sidebar) stays usable —
 * built for "read this image while typing values into the form". Drag by the
 * title bar; resize from the bottom-right grip.
 */
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;
const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));

const FloatingImageViewer: React.FC<FloatingImageViewerProps> = ({ open, src, title, onClose, isPdf = false, onSaveRotation, footer, embedded = false }) => {
  const styles = useStyles();
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragState = useRef<{ dx: number; dy: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  // Net clockwise rotation the user has dialed in but not yet saved. Reset once
  // the displayed src changes (a save reloads fresh, already-rotated bytes).
  const [rotation, setRotation] = useState(0);
  const [saving, setSaving] = useState(false);
  // Body size, tracked so a rotated PDF iframe can swap its dimensions to keep
  // filling the frame at 90°/270° (an <iframe> can't be `object-fit`).
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodySize, setBodySize] = useState({ w: 0, h: 0 });

  // Reset zoom + rotation whenever the window closes or the displayed image
  // changes, so a freshly opened image always starts fit-to-window, unrotated.
  useEffect(() => {
    setZoom(1);
    setRotation(0);
  }, [src, open]);

  // Track the body's live size for the rotated-PDF dimension swap.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      setBodySize({ w: el.clientWidth, h: el.clientHeight });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, isPdf]);

  const canRotate = !!onSaveRotation;
  const rotateBy = (delta: number) => setRotation((r) => (((r + delta) % 360) + 360) % 360);
  const handleSaveRotation = useCallback(async () => {
    if (!onSaveRotation || rotation === 0 || saving) return;
    setSaving(true);
    try {
      await onSaveRotation(rotation);
    } finally {
      setSaving(false);
    }
  }, [onSaveRotation, rotation, saving]);

  // Place the window once when it opens: offset toward the left/center so it
  // doesn't cover the right sidebar where the tonnage fields live.
  useEffect(() => {
    if (!open) return;
    if (pos) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const x = Math.max(16, Math.round(vw * 0.32 - DEFAULT_SIZE.w / 2));
    const y = Math.max(64, Math.round(vh / 2 - DEFAULT_SIZE.h / 2));
    setPos({ x, y });
  }, [open, pos]);

  // Reset placement after it fully closes so the next open re-centers.
  useEffect(() => {
    if (!open) setPos(null);
  }, [open]);

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (!dragState.current) return;
    const x = e.clientX - dragState.current.dx;
    const y = e.clientY - dragState.current.dy;
    // Keep at least a sliver on-screen so it can't be lost off an edge.
    const clampedX = Math.min(Math.max(x, -DEFAULT_SIZE.w + 80), window.innerWidth - 80);
    const clampedY = Math.min(Math.max(y, 8), window.innerHeight - 48);
    setPos({ x: clampedX, y: clampedY });
  }, []);

  const onPointerUp = useCallback(() => {
    dragState.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  const onTitlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!pos) return;
      // Ignore drags that start on the close button.
      if ((e.target as HTMLElement).closest('button')) return;
      dragState.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    },
    [pos, onPointerMove, onPointerUp],
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  const zoomIn = useCallback(() => setZoom((z) => clampZoom(z + ZOOM_STEP)), []);
  const zoomOut = useCallback(() => setZoom((z) => clampZoom(z - ZOOM_STEP)), []);
  const zoomReset = useCallback(() => setZoom(1), []);

  // Wheel-to-zoom over the image area. preventDefault stops the page/scroll
  // from also moving; the smaller step gives finer trackpad control.
  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => clampZoom(z + (e.deltaY < 0 ? ZOOM_STEP / 2 : -ZOOM_STEP / 2)));
  }, []);

  if (!open || !src) return null;
  // Floating mode needs a resolved position before it can paint; embedded fills
  // its container, so it has nothing to wait on.
  if (!embedded && !pos) return null;

  return (
    <div
      className={embedded ? mergeClasses(styles.window, styles.windowEmbedded) : styles.window}
      style={embedded ? undefined : { left: pos!.x, top: pos!.y, width: DEFAULT_SIZE.w, height: DEFAULT_SIZE.h }}
      role="dialog"
      aria-label={title || 'Image viewer'}
    >
      <div
        className={embedded ? mergeClasses(styles.titleBar, styles.titleBarStatic) : styles.titleBar}
        onPointerDown={embedded ? undefined : onTitlePointerDown}
      >
        <ReOrderDotsVertical20Regular className={styles.dragIcon} />
        <Text className={styles.title}>{title || (isPdf ? 'PDF' : 'Image')}</Text>
        {!isPdf && (
          <>
            <Button
              appearance="subtle"
              size="small"
              icon={<ZoomOut16Regular />}
              title="Zoom out"
              aria-label="Zoom out"
              disabled={zoom <= ZOOM_MIN}
              onClick={zoomOut}
            />
            <Text className={styles.zoomLabel}>{Math.round(zoom * 100)}%</Text>
            <Button
              appearance="subtle"
              size="small"
              icon={<ZoomIn16Regular />}
              title="Zoom in"
              aria-label="Zoom in"
              disabled={zoom >= ZOOM_MAX}
              onClick={zoomIn}
            />
            <Button
              appearance="subtle"
              size="small"
              icon={<ArrowReset20Regular />}
              title="Reset zoom"
              aria-label="Reset zoom"
              disabled={zoom === 1}
              onClick={zoomReset}
            />
          </>
        )}
        {canRotate && (
          <>
            <Button
              appearance="subtle"
              size="small"
              icon={<ArrowRotateCounterclockwise20Regular />}
              title="Rotate left"
              aria-label="Rotate left"
              disabled={saving}
              onClick={() => rotateBy(-90)}
            />
            <Button
              appearance="subtle"
              size="small"
              icon={<ArrowRotateClockwise20Regular />}
              title="Rotate right"
              aria-label="Rotate right"
              disabled={saving}
              onClick={() => rotateBy(90)}
            />
            {/* Always mounted (in rotatable mode) so the row never reflows when a
                rotation is dialed in — it holds its reserved slot, disabled, until
                there's a pending rotation to save. Mounting it on demand shrank the
                flex-grow title and bumped the rotate buttons over. */}
            <Button
              appearance="primary"
              size="small"
              icon={saving ? <Spinner size="tiny" /> : <Save20Regular />}
              title="Save rotation"
              aria-label="Save rotation"
              disabled={saving || rotation === 0}
              onClick={handleSaveRotation}
            >
              Save
            </Button>
          </>
        )}
        <Button
          appearance="subtle"
          size="small"
          icon={<Open16Regular />}
          title="Open in new tab"
          aria-label="Open in new tab"
          onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
        />
        <Button
          appearance="subtle"
          size="small"
          icon={<Dismiss20Regular />}
          title="Close"
          aria-label="Close image viewer"
          onClick={onClose}
        />
      </div>
      <div className={styles.body} ref={bodyRef} onWheel={isPdf ? undefined : onWheel}>
        {isPdf ? (
          <iframe
            className={styles.frame}
            src={src}
            title={title || 'PDF'}
            // At 90°/270° the iframe is rotated, so swap its box to the body's
            // (h × w) and it fills the frame after the transform.
            style={
              rotation % 180 === 0
                ? { transform: `rotate(${rotation}deg)` }
                : {
                    transform: `rotate(${rotation}deg)`,
                    width: `${bodySize.h}px`,
                    height: `${bodySize.w}px`,
                  }
            }
          />
        ) : (
          <img
            className={styles.image}
            src={src}
            alt={title || 'Attachment'}
            draggable={false}
            style={{ transform: `rotate(${rotation}deg) scale(${zoom})` }}
          />
        )}
      </div>
      {footer != null && <div className={styles.footer}>{footer}</div>}
    </div>
  );
};

export default FloatingImageViewer;
